import { useAuth } from '@clerk/react'
import { CheckCircle } from 'lucide-react'
import { Copyright } from '@/components/ui/copyright'
import { SyntaxHighlightedMarkdown } from '@/components/ui/syntax-highlighted-markdown'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { getInterviewingioUrl } from '@/lib/interviewingio'
import { trpc } from '@/lib/trpc'
import { ExampleCard } from './example-card'
import { useProblem } from './problem-context'
import { SourceAttribution, useProblemAttribution } from './source-attribution'
import { TopicsSection } from './topics-section'

export interface DescriptionPaneProps {
  /**
   * Whether to credit the problem's source in the title row.
   *
   * Off for an interview candidate: "this is LeetCode 3Sum" hands them the
   * answer mid-mock. Every other surface shows it, so the default is on and
   * only the interview surface opts out.
   */
  showSourceAttribution?: boolean
}

export function DescriptionPane({
  showSourceAttribution = true,
}: DescriptionPaneProps = {}) {
  const { data, sections, problemSlug } = useProblem()
  const { isSignedIn } = useAuth()
  const interviewingioFlag = useFeatureFlag('enable-interviewingio', {
    skipAdminBypass: true,
  })

  const solveCountQuery = trpc.problems.getSolveCount.useQuery(
    { problemId: problemSlug! },
    { enabled: !!problemSlug && !!isSignedIn },
  )
  const solveCount = solveCountQuery.data?.count ?? 0

  // Drives two things: the credit widget, and how much the copyright notice
  // claims. Read once here rather than inside each, so the pane can't show a
  // blanket copyright beside a widget crediting someone else.
  //
  // Gated on the same prop that hides the widget, not just rendered behind it:
  // an interview candidate's browser would otherwise still fetch and hold
  // `{original: {name: "LeetCode", url: …}}`, which is the answer the prop
  // exists to withhold, one devtools tab away.
  const { attribution, copyrightScope } = useProblemAttribution(problemSlug, {
    enabled: showSourceAttribution,
  })

  if (sections.statement.status === 'loading') {
    return (
      <div className="p-6">
        <div className="mb-4 h-8 w-48 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        <div className="space-y-2">
          <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    )
  }

  if (sections.statement.status === 'error') {
    return (
      <div className="p-6">
        <p className="text-center text-red-500">{sections.statement.message}</p>
      </div>
    )
  }

  if (!problemSlug || !data?.statement) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground text-center">No problem selected</p>
      </div>
    )
  }

  const Description = data.statement
  const title = data.title ?? 'Untitled Problem'

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-3xl p-6">
        {/* Problem Title */}
        <div className="mb-4 flex items-center justify-between gap-4">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h1>
          <div className="flex shrink-0 items-center gap-3">
            {solveCount >= 1 && (
              <span className="inline-flex shrink-0 items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                Solved {solveCount} {solveCount === 1 ? 'time' : 'times'}
                <CheckCircle className="h-4 w-4 text-green-500" />
              </span>
            )}
            {showSourceAttribution && attribution && (
              <SourceAttribution attribution={attribution} />
            )}
          </div>
        </div>

        {/* Problem Statement */}
        <section className="mb-6">
          <article className="prose prose-sm dark:prose-invert max-w-none [&_code]:bg-gray-200 dark:[&_code]:bg-gray-700">
            <Description />
          </article>
        </section>

        {/* Examples Section */}
        <section className="border-t border-gray-200 py-6 dark:border-gray-700">
          {data.examples && data.examples.length > 0 ? (
            data.examples.map((example) => (
              <ExampleCard key={example.index} example={example} />
            ))
          ) : (
            <p className="text-muted-foreground text-sm italic">
              No examples available.
            </p>
          )}
        </section>

        {/* Constraints Section */}
        {data.constraints && data.constraints.length > 0 && (
          <section className="border-t border-gray-200 py-6 dark:border-gray-700">
            <p className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
              Constraints:
            </p>
            <ul className="list-disc space-y-2 pl-6 marker:text-foreground/70">
              {data.constraints.map((constraint) => (
                <li key={constraint}>
                  <SyntaxHighlightedMarkdown className="prose prose-sm dark:prose-invert inline max-w-none [&_code]:bg-gray-200 [&_p]:inline dark:[&_code]:bg-gray-700">
                    {constraint}
                  </SyntaxHighlightedMarkdown>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Follow-up Section */}
        {data.followUps && data.followUps.length > 0 && (
          <section className="border-t border-gray-200 py-6 dark:border-gray-700">
            <p className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
              Follow-up
            </p>
            <ol className="list-decimal space-y-2 pl-6 marker:text-foreground/70">
              {data.followUps.map((followUp) => (
                <li key={followUp}>
                  <SyntaxHighlightedMarkdown className="prose prose-sm dark:prose-invert inline max-w-none [&_code]:bg-gray-200 [&_p]:inline dark:[&_code]:bg-gray-700">
                    {followUp}
                  </SyntaxHighlightedMarkdown>
                </li>
              ))}
            </ol>
          </section>
        )}

        {interviewingioFlag.isLoaded && interviewingioFlag.enabled && (
          <section className="border-t border-gray-200 py-6 dark:border-gray-700">
            <a
              href={getInterviewingioUrl(problemSlug)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
            >
              <img
                src="/icons/interviewing-io-sparkle-comment.webp"
                alt=""
                aria-hidden="true"
                className="h-5 w-5"
              />
              Get asked this question by our AI Interviewer
            </a>
          </section>
        )}

        <TopicsSection />

        {/* Renders only on a problem BCTCI both owns and posed; on anything
            that originated elsewhere `Copyright` renders nothing at all,
            rather than a narrower claim over the statement and test cases.
            `copyrightScopeFor` resolves the not-yet-known case to silence, so
            a failed or in-flight query never prints a claim. */}
        <Copyright scope={copyrightScope} />
      </div>
    </div>
  )
}
